import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Clock, FileX2, ShieldCheck, Lock, ArrowRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Marquee } from "@/components/Marquee";

const HERO_IMG =
  "https://images.pexels.com/photos/27165073/pexels-photo-27165073.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ABOUT_IMG =
  "https://images.pexels.com/photos/3993455/pexels-photo-3993455.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FEATURE_IMG =
  "https://images.pexels.com/photos/7750125/pexels-photo-7750125.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const HEADLINE = ["What Could", "Your Hair Salon", "Be Worth?"];

const POINTS = [
  { icon: Clock, label: "Takes about two minutes" },
  { icon: FileX2, label: "No detailed financial records" },
  { icon: ShieldCheck, label: "No obligation" },
  { icon: Lock, label: "Confidential indicative result" },
];

const CHAPTERS = [
  {
    n: "01",
    title: "Answer a few simple questions",
    body: "A short, anonymous set of questions about your salon's earnings, revenue, team, capacity and lease. No accounting jargon.",
  },
  {
    n: "02",
    title: "We calculate your earnings",
    body: "We work out your Seller's Discretionary Earnings — the true income flowing to one working owner — the number buyers value most.",
  },
  {
    n: "03",
    title: "See your indicative value",
    body: "An indicative valuation range is produced from your earnings and a market-based multiple, cross-checked against your fit-out value.",
  },
  {
    n: "04",
    title: "Unlock your full report",
    body: "Leave your details to reveal the full breakdown and receive a confidential copy by email. Then decide your next move on your terms.",
  },
];

const RevealLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
  >
    {children}
  </motion.div>
);

export default function Landing() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const start = () => navigate("/valuation");

  return (
    <div className="bg-background">
      <Nav />

      {/* HERO */}
      <section ref={heroRef} className="relative grain overflow-hidden">
        <div className="relative z-[2] max-w-[1400px] mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-16 md:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="eyebrow text-brand mb-6"
              >
                Salons For Sale
              </motion.div>
              <h1 className="font-serif font-light tracking-tight text-[3.2rem] leading-[0.95] sm:text-7xl lg:text-[6.2rem]">
                {HEADLINE.map((line, i) => (
                  <RevealLine key={i} delay={0.35 + i * 0.12}>
                    {line}
                  </RevealLine>
                ))}
              </h1>
            </div>

            <div className="lg:col-span-5">
              <FadeUp delay={0.9}>
                <p className="text-base md:text-lg text-muted-foreground max-w-md leading-relaxed">
                  Answer a few simple questions to receive an indicative
                  valuation based on your salon&rsquo;s earnings, revenue, team,
                  unused capacity and lease.
                </p>
                <button
                  data-testid="hero-start-button"
                  onClick={start}
                  className="group mt-8 inline-flex items-center gap-3 rounded-full bg-brand text-brand-foreground pl-7 pr-6 py-4 eyebrow hover:bg-primary transition-colors duration-300"
                >
                  Start My Free Valuation
                  <span className="grid place-items-center w-8 h-8 rounded-full bg-white/20 transition-transform duration-300 group-hover:translate-x-1">
                    <ArrowRight size={16} />
                  </span>
                </button>
              </FadeUp>
            </div>
          </div>
        </div>

        {/* Clipped parallax image band */}
        <div className="relative z-[2] max-w-[1400px] mx-auto px-6 md:px-10 pb-12">
          <div className="relative h-[42vh] md:h-[56vh] overflow-hidden">
            <motion.img
              src={HERO_IMG}
              alt="Contemporary hair salon interior"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-primary/20" />
            <div className="absolute bottom-6 left-6 right-6 flex flex-wrap gap-x-8 gap-y-3">
              {POINTS.map(({ icon: Icon, label }, i) => (
                <FadeUp key={i} delay={1 + i * 0.08}>
                  <div className="flex items-center gap-2.5 text-white">
                    <Icon size={16} className="text-white/90" />
                    <span className="eyebrow text-white/95">{label}</span>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* HOW IT WORKS — numbered manifesto */}
      <section id="how" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <FadeUp>
              <div className="eyebrow text-brand mb-5">The Process</div>
              <h2 className="font-serif font-light text-5xl md:text-6xl leading-[0.95] tracking-tight lg:sticky lg:top-28">
                Four steps.
                <br />
                About two
                <br />
                minutes.
              </h2>
            </FadeUp>
          </div>
          <div className="lg:col-span-8 lg:col-start-5">
            <div className="border-t border-border">
              {CHAPTERS.map((c, i) => (
                <FadeUp key={c.n} delay={i * 0.06}>
                  <div className="grid grid-cols-12 gap-4 py-10 border-b border-border group">
                    <div className="col-span-2 font-serif text-3xl md:text-4xl text-brand">
                      {c.n}
                    </div>
                    <div className="col-span-10">
                      <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-3 transition-colors group-hover:text-brand">
                        {c.title}
                      </h3>
                      <p className="text-muted-foreground max-w-xl leading-relaxed">
                        {c.body}
                      </p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE BENTO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-36">
        <div className="grid md:grid-cols-12 gap-6">
          <FadeUp className="md:col-span-7">
            <div className="relative h-[380px] md:h-[520px] overflow-hidden">
              <img
                src={FEATURE_IMG}
                alt="Stylish hair salon with sleek chairs"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/70 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8 text-white">
                <div className="eyebrow text-white/70 mb-3">Valued the way buyers value</div>
                <h3 className="font-serif text-4xl md:text-5xl leading-[0.95] tracking-tight max-w-lg">
                  Earnings to one working owner
                </h3>
              </div>
            </div>
          </FadeUp>

          <div className="md:col-span-5 flex flex-col gap-6">
            <FadeUp delay={0.1}>
              <div className="bg-primary text-primary-foreground p-8 md:p-10">
                <div className="font-serif text-6xl md:text-7xl text-brand leading-none">4%</div>
                <p className="mt-4 text-primary-foreground/70 leading-relaxed">
                  A low commission when it&rsquo;s time to sell &mdash; a fraction
                  of what most brokers charge.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="border border-border p-8 md:p-10 bg-card">
                <div className="font-serif text-6xl md:text-7xl leading-none tracking-tight">
                  2 min
                </div>
                <p className="mt-4 text-muted-foreground leading-relaxed">
                  A confidential indicative valuation with no detailed financial
                  records and no obligation.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="bg-primary text-primary-foreground grain relative">
        <div className="relative z-[2] max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <FadeUp className="lg:col-span-5">
              <div className="relative h-[420px] md:h-[560px] overflow-hidden">
                <img
                  src={ABOUT_IMG}
                  alt="Professional hairstylist"
                  className="w-full h-full object-cover"
                />
              </div>
            </FadeUp>
            <div className="lg:col-span-6 lg:col-start-7">
              <FadeUp>
                <div className="eyebrow text-brand mb-6">About Us</div>
                <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.0] tracking-tight">
                  Specialists in the sale of hair &amp; beauty salons.
                </h2>
                <p className="mt-8 text-primary-foreground/70 text-lg leading-relaxed max-w-xl">
                  Salons For Sale, in association with Bonza Business &amp; Franchise
                  Sales, understands what makes a salon valuable &mdash; and what
                  buyers look for. This tool gives you a quick, private sense of
                  where you stand, before you decide whether to take it further.
                </p>
                <button
                  data-testid="about-start-button"
                  onClick={start}
                  className="group mt-10 inline-flex items-center gap-3 rounded-full border border-white/25 text-white pl-7 pr-6 py-4 eyebrow hover:bg-brand hover:border-brand transition-colors duration-300"
                >
                  Value My Salon
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
